import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePaginationParams } from '../../../hooks/usePaginationParams';
import { Search, Bot } from 'lucide-react';
import { useDomains } from '../hooks/useDomains';
import { EmptyState } from '../../../components/common';
import { DomainSummary, DomainStatus, SyncStatus } from '../../../types';
import { formatNumber } from '../../../lib/utils';
import { getDomainDisplayStatus } from '../../../lib/domainStatus';
import { ROUTES } from '../../../config/routes';

interface DomainListProps {
  onDomainClick?: (domain: DomainSummary) => void;
}

const AVATAR_GRADIENTS = [
  'from-teal-400 to-teal-600',
  'from-blue-400 to-blue-600',
  'from-purple-400 to-indigo-600',
  'from-rose-400 to-pink-600',
];

const STATUS_PILLS = [
  { label: 'すべて', value: 'all' },
  { label: '稼働中', value: 'active' },
  { label: '学習中', value: 'learning' },
  { label: '停止中', value: 'inactive' },
] as const;

type FilterStatus = 'all' | 'active' | 'learning' | 'inactive';

const StatusBadge: React.FC<{ status: DomainStatus; syncStatus: SyncStatus | null }> = ({ status, syncStatus }) => {
  const displayStatus = getDomainDisplayStatus(status, syncStatus);
  if (displayStatus === 'active') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-[20px] text-xs font-bold bg-[#DCFCE7] text-[#166534]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#166534]" />
        稼働中
      </span>
    );
  }
  if (displayStatus === 'learning') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-[20px] text-xs font-bold bg-[#FEF3C7] text-[#92400E]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#92400E]" />
        学習中...
      </span>
    );
  }
  if (displayStatus === 'error') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-[20px] text-xs font-bold bg-[#FEE2E2] text-[#991B1B]">
        <span className="w-1.5 h-1.5 rounded-full bg-[#991B1B]" />
        エラー
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-[20px] text-xs font-bold bg-gray-100 text-gray-500">
      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
      停止中
    </span>
  );
};

export const DomainList: React.FC<DomainListProps> = ({ onDomainClick }) => {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [totalPages, setTotalPages] = useState<number | undefined>(undefined);
  const { page, setPage } = usePaginationParams({ totalPages });
  const apiStatus = statusFilter === 'learning' ? 'inactive' : statusFilter;

  const { data, isLoading } = useDomains({
    page,
    limit: 20,
    status: apiStatus as 'all' | 'active' | 'inactive',
    search: search || undefined,
  });

  useEffect(() => {
    const total_pages = data?.pagination?.total_pages;
    setTotalPages(total_pages);
  }, [data?.pagination?.total_pages]); // eslint-disable-line react-hooks/exhaustive-deps

  const domains = data?.data || [];
  const totalCount = data?.pagination?.total ?? domains.length;

  const handleDomainClick = (domain: DomainSummary) => {
    if (onDomainClick) {
      onDomainClick(domain);
    } else {
      navigate(`/chatbots/${domain.id}`);
    }
  };

  if (isLoading) {
    return (
      <div data-testid="domain-list-skeleton">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-200 rounded-[10px] h-48" />
          ))}
        </div>
      </div>
    );
  }

  if (domains.length === 0 && !search && statusFilter === 'all') {
    return (
      <div data-testid="domain-list-empty">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-text-primary">チャットボット</h1>
          <p className="text-sm text-text-secondary mt-1">サイトに設置するチャットボットを作成・管理します。</p>
        </div>
        <div className="bg-white rounded-xl border border-border p-12 flex flex-col items-center text-center">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
            style={{ backgroundColor: '#E6F7F5' }}
          >
            <Bot className="h-8 w-8" style={{ color: '#0a7a72' }} />
          </div>
          <h2 className="text-lg font-bold text-text-primary mb-3">最初のチャットボットを作成しましょう</h2>
          <p className="text-sm text-text-secondary max-w-sm mb-8">
            サイトのURLを入力するだけで、AIがページを学習し、お客様の質問に自動で答えるチャットボットを作ります。約5分で完了します。
          </p>
          <button
            type="button"
            onClick={() => navigate(ROUTES.DOMAIN_NEW)}
            data-testid="empty-state-create-bot"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white rounded-lg hover:opacity-90 transition-opacity"
            style={{ backgroundColor: '#0a7a72' }}
          >
            + チャットボットを作成
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* [B] フィルター + 検索（横並び） */}
      <div className="mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex gap-2 flex-wrap">
          {STATUS_PILLS.map(({ label, value }) => (
            <button
              key={value}
              type="button"
              onClick={() => { setStatusFilter(value); setPage(1); }}
              className={`px-4 py-1.5 text-[13px] rounded-[20px] border transition-colors ${
                statusFilter === value
                  ? 'text-white border-transparent'
                  : 'bg-white border-border text-text-secondary hover:border-primary/50'
              }`}
              style={statusFilter === value ? { backgroundColor: '#0a7a72', borderColor: '#0a7a72' } : {}}
              data-testid={`domain-status-filter-${value}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="relative md:ml-auto md:w-64">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <Search className="h-4 w-4" />
          </span>
          <input
            type="text"
            placeholder="ボットを検索"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 border border-border rounded-lg text-sm w-full outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            data-testid="domain-search-input"
          />
        </div>
      </div>

      {/* [C] ボット一覧テーブル */}
      {domains.length === 0 ? (
        <EmptyState
          title="該当するチャットBOTがありません"
          description="検索条件やフィルターを変更してお試しください"
        />
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid="domain-list-content">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-gray-50">
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary">ボット名</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary">ドメイン</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary">ステータス</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary">今月の訪問者数</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary">今月の利用者数</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary">リード</th>
              </tr>
            </thead>
            <tbody>
              {domains.map((domain, index) => (
                <tr
                  key={domain.id}
                  className="border-b border-border last:border-b-0 hover:bg-gray-50 transition-colors cursor-pointer"
                  data-testid={`domain-card-${domain.id}`}
                  onClick={() => handleDomainClick(domain)}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 rounded-lg bg-gradient-to-br ${AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length]} flex items-center justify-center flex-shrink-0 text-white`}
                      >
                        <Bot className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-text-primary truncate">
                          {domain.description || domain.domain}
                        </p>
                        <p className="text-xs text-text-secondary truncate">{domain.domain}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-text-secondary">{domain.domain}</td>
                  <td className="px-6 py-4">
                    <StatusBadge status={domain.status} syncStatus={domain.sync_status} />
                  </td>
                  <td className="px-6 py-4 text-text-primary">{formatNumber(domain.visitors_this_month ?? 0)}</td>
                  <td className="px-6 py-4 text-text-primary">{formatNumber(domain.chat_users_this_month)}</td>
                  <td className="px-6 py-4 text-text-primary">{formatNumber(domain.leads_this_month ?? 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* [D] ページネーション */}
      {totalCount > 0 && (
        <div className="mt-4 flex items-center justify-between">
          <p className="text-sm text-text-secondary">
            全{totalCount}件中 {(page - 1) * 20 + 1}-{Math.min(page * 20, totalCount)}件を表示
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              前へ
            </button>
            {totalPages && Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => setPage(pageNum)}
                  className={`w-9 h-9 text-sm rounded-md transition-colors ${
                    page === pageNum
                      ? 'text-white'
                      : 'border border-border hover:bg-gray-50'
                  }`}
                  style={page === pageNum ? { backgroundColor: '#0a7a72' } : {}}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              type="button"
              disabled={!totalPages || page >= totalPages}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              次へ
            </button>
          </div>
        </div>
      )}

    </div>
  );
};
